import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  InputAdornment,
  TextField,
} from '@mui/material'
import { yupResolver } from '@hookform/resolvers/yup'
import {
  type JSX,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  Controller,
  FormProvider,
  useForm,
  type Resolver,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { boolean, number, object, string, type AnySchema } from 'yup'
import type { Device } from '@/pages/Device/Device'
import {
  type Ba,
  type CalibrationData,
  formatBa,
  type ResistanceBox,
  resistanceBoxLabel,
} from '../calibration'
import type { Composition } from '../composition'
import {
  ZKL_3000,
  ZKL_3000_RC,
  ZKL_3000_RCC,
  ZKL_3000_RC_AFTC,
} from '@/pages/Device/SupportedTypes'
import { getZklRcCalibration } from '@/api/calibration'
import useResponseValidator from '@/pages/utils/responseValidator'
import { CalibrationSchema } from '../schema'
import ConfirmDialog from '@/components/ConfirmDialogBox'
import VerifyStepBase from './VerifyStepBase'
import {
  formLegendSx,
  labelNoErrorColor,
  requiredCheckboxGroupItemSx,
  requiredCheckboxLabelTextSx,
  requiredCheckboxSx,
  requiredIndicator,
} from '@/utils/theme'
import useFinishVerification from '@/hooks/useFinishVerfication'
import { showFieldError } from '@/components/SharedPreFlightFields'

const MAX_SHORT_CIRCUIT_WIRE_RESISTANCE = 9.5

interface Props {
  device: Device
  composition: Composition | null
  setComposition: (c: Composition | null) => void
  resistanceBox: ResistanceBox | null
  setResistanceBox: (rb: ResistanceBox | null) => void
  jumperBox: string | null
  setJumperBox: (jb: string | null) => void
  ba: Ba
  setBa: (ba: Ba) => void
  dropOff: string
  setDropOff: (d: string) => void
  setActiveStep: (step: number) => void
  finishSelfTest: (message: Record<string, unknown>) => void
  lastJsonMessage: Record<string, unknown> | null
}

type FormValues = {
  baValue: number | ''
  resistanceBox: string
  jumperBox: string
  dropOff: string
  greenLedOK: boolean
  redLedOK: boolean
  redLedOnOffOK: boolean
  AFTCMOSFETOk: boolean
  shortCircuitDetected: boolean
  shortCircuitWire: number | ''
  shortCircuitRemoved: boolean
  remarks: string
}

const ZklVerifyStep = ({
  device,
  composition,
  setActiveStep,
  resistanceBox,
  jumperBox,
  ba,
  setBa,
  dropOff,
  finishSelfTest,
  lastJsonMessage,
}: Props): JSX.Element => {
  const { t } = useTranslation(['app', 'common'])
  const { validate } = useResponseValidator()
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [updatedBa, setUpdatedBa] = useState<Ba | undefined>(undefined)
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false)

  const isNonRC = device.type === ZKL_3000
  const isRC = [ZKL_3000_RC, ZKL_3000_RCC, ZKL_3000_RC_AFTC].includes(
    device.type,
  )
  const hasAFTC = device.type === ZKL_3000_RC_AFTC

  const validateRef = useRef(validate)
  useEffect(() => {
    validateRef.current = validate
  })

  const setBaRef = useRef(setBa)
  useEffect(() => {
    setBaRef.current = setBa
  })

  // Re-fetch BA after calibration test
  const fetchCalibration = useCallback(async () => {
    try {
      const response = await getZklRcCalibration(device.uid)
      const { calibration } = await validateRef.current<CalibrationData>(
        CalibrationSchema,
        response,
      )
      setUpdatedBa(calibration.ba)
      setBaRef.current(calibration.ba)
    } catch {
      // Caught by interceptor
    }
  }, [device.uid])

  useEffect(() => {
    void fetchCalibration()
  }, [fetchCalibration])

  const schema = useMemo(() => {
    const shape: Record<string, AnySchema> = {
      baValue: number().required(),
      resistanceBox: string().optional(),
      jumperBox: string().optional(),
      dropOff: string().optional(),
      greenLedOK: boolean().equals([true], '').required(),
      redLedOK: boolean().equals([true], '').required(),
      redLedOnOffOK: isNonRC
        ? boolean().equals([true], '').required()
        : boolean().optional(),
      AFTCMOSFETOk: hasAFTC
        ? boolean().equals([true], '').required()
        : boolean().optional(),
      shortCircuitDetected: boolean().equals([true], '').required(),
      shortCircuitWire: number()
        .transform((value, originalValue) =>
          originalValue === '' ? undefined : value,
        )
        .required('')
        .min(0)
        .max(MAX_SHORT_CIRCUIT_WIRE_RESISTANCE),
      shortCircuitRemoved: isRC
        ? boolean().equals([true], '').required()
        : boolean().optional(),
      remarks: string().optional(),
    }
    return object(shape)
  }, [isNonRC, hasAFTC, isRC])

  const effectiveBa = updatedBa ?? ba

  const methods = useForm<FormValues>({
    resolver: yupResolver(schema) as unknown as Resolver<FormValues>,
    values: {
      baValue: formatBa(effectiveBa),
      resistanceBox: resistanceBox ? resistanceBoxLabel(resistanceBox) : '',
      jumperBox: jumperBox ?? '',
      dropOff,
      greenLedOK: false,
      redLedOK: false,
      redLedOnOffOK: false,
      AFTCMOSFETOk: false,
      shortCircuitDetected: false,
      shortCircuitWire: '',
      shortCircuitRemoved: false,
      remarks: '',
    },
    resetOptions: { keepDirtyValues: true },
    mode: 'all',
  })

  const { isSaving, confirm } = useFinishVerification({
    device,
    composition,
    finishSelfTest,
    lastJsonMessage,
    setActiveStep,
  })

  const { serial, label, type } = device

  return (
    <FormProvider {...methods}>
      <VerifyStepBase
        serial={serial ?? ''}
        label={label}
        type={type}
        isSaving={isSaving}
        composition={composition}
        onBack={() => setActiveStep(2)}
        onFormSubmit={(e) => {
          setHasAttemptedSubmit(true)
          void methods.handleSubmit(() => {
            setShowConfirmDialog(true)
          })(e)
        }}
      >
        {/* Read-only equipment fields */}
        <FormGroup sx={{ mb: 2 }}>
          {updatedBa === undefined && (
            <Alert
              severity="info"
              icon={<CircularProgress size="1rem" />}
              sx={{ mb: 1 }}
            >
              {t('app:device.calibration.zklRc.refreshingBa')}
            </Alert>
          )}
          <Controller
            name="baValue"
            control={methods.control}
            render={({ field }) => (
              <TextField
                {...field}
                label={t('app:device.calibration.zklRc.baValue')}
                fullWidth
                variant="standard"
                disabled
                sx={{ mb: 2 }}
              />
            )}
          />
          <Controller
            name="resistanceBox"
            control={methods.control}
            render={({ field }) => (
              <TextField
                {...field}
                label={t('app:device.calibration.zklRc.resistanceBox')}
                fullWidth
                variant="standard"
                disabled
                sx={{ mb: 2 }}
              />
            )}
          />
          <Controller
            name="jumperBox"
            control={methods.control}
            render={({ field }) => (
              <TextField
                {...field}
                label={t('app:device.calibration.zklRc.switchBox')}
                fullWidth
                variant="standard"
                disabled
                sx={{ mb: 2 }}
              />
            )}
          />
          <Controller
            name="dropOff"
            control={methods.control}
            render={({ field }) => (
              <TextField
                {...field}
                label={t('app:device.calibration.zklRc.calibrationDropOff')}
                fullWidth
                variant="standard"
                disabled
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">{'<'}</InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">mOhm</InputAdornment>
                    ),
                  },
                }}
                sx={{ mb: 2 }}
              />
            )}
          />
        </FormGroup>

        {/* During calibration verification */}
        <FormGroup sx={{ mb: 2 }}>
          <FormLabel component="legend" sx={formLegendSx}>
            {t('app:device.calibration.verifyStep.zklRc.duringCalibration')}
          </FormLabel>

          {(['greenLedOK', 'redLedOK'] as const).map((name) => (
            <Controller
              key={name}
              name={name}
              control={methods.control}
              render={({ field, fieldState }) => {
                const hasError = hasAttemptedSubmit && Boolean(fieldState.error)
                return (
                  <FormControl
                    error={hasError}
                    sx={requiredCheckboxGroupItemSx}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(field.value)}
                          onChange={(e) => field.onChange(e.target.checked)}
                          sx={requiredCheckboxSx(hasError)}
                        />
                      }
                      label={
                        <>
                          <Box
                            component="span"
                            sx={requiredCheckboxLabelTextSx}
                          >
                            {t(
                              `app:device.calibration.verifyStep.zklRc.${name}`,
                            )}
                          </Box>
                          <Box
                            component="span"
                            sx={{ color: 'error.main', ml: 0.5 }}
                          >
                            *
                          </Box>
                        </>
                      }
                    />
                    {hasAttemptedSubmit && showFieldError(fieldState) && (
                      <FormHelperText sx={{ ml: 4 }}>
                        {showFieldError(fieldState)}
                      </FormHelperText>
                    )}
                  </FormControl>
                )
              }}
            />
          ))}

          {isNonRC && (
            <Controller
              name="redLedOnOffOK"
              control={methods.control}
              render={({ field, fieldState }) => {
                const hasError = hasAttemptedSubmit && Boolean(fieldState.error)
                return (
                  <FormControl
                    error={hasError}
                    sx={requiredCheckboxGroupItemSx}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(field.value)}
                          onChange={(e) => field.onChange(e.target.checked)}
                          sx={requiredCheckboxSx(hasError)}
                        />
                      }
                      label={
                        <>
                          <Box
                            component="span"
                            sx={requiredCheckboxLabelTextSx}
                          >
                            {t(
                              'app:device.calibration.verifyStep.zklRc.redLedOnOffOK',
                            )}
                          </Box>
                          <Box
                            component="span"
                            sx={{ color: 'error.main', ml: 0.5 }}
                          >
                            *
                          </Box>
                        </>
                      }
                    />
                    {hasAttemptedSubmit && showFieldError(fieldState) && (
                      <FormHelperText sx={{ ml: 4 }}>
                        {showFieldError(fieldState)}
                      </FormHelperText>
                    )}
                  </FormControl>
                )
              }}
            />
          )}

          {hasAFTC && (
            <Controller
              name="AFTCMOSFETOk"
              control={methods.control}
              render={({ field, fieldState }) => {
                const hasError = hasAttemptedSubmit && Boolean(fieldState.error)
                return (
                  <FormControl
                    error={hasError}
                    sx={requiredCheckboxGroupItemSx}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(field.value)}
                          onChange={(e) => field.onChange(e.target.checked)}
                          sx={requiredCheckboxSx(hasError)}
                        />
                      }
                      label={
                        <>
                          <Box
                            component="span"
                            sx={requiredCheckboxLabelTextSx}
                          >
                            {t(
                              'app:device.calibration.verifyStep.zklRc.AFTCMOSFETOk',
                            )}
                          </Box>
                          <Box
                            component="span"
                            sx={{ color: 'error.main', ml: 0.5 }}
                          >
                            *
                          </Box>
                        </>
                      }
                    />
                    {hasAttemptedSubmit && showFieldError(fieldState) && (
                      <FormHelperText sx={{ ml: 4 }}>
                        {showFieldError(fieldState)}
                      </FormHelperText>
                    )}
                  </FormControl>
                )
              }}
            />
          )}
        </FormGroup>

        {/* Short circuit test */}
        <FormGroup sx={{ mb: 2 }}>
          <FormLabel component="legend" sx={formLegendSx}>
            {t('app:device.calibration.verifyStep.zklRc.shortCircuitTest')}
          </FormLabel>

          <Controller
            name="shortCircuitDetected"
            control={methods.control}
            render={({ field, fieldState }) => {
              const hasError = hasAttemptedSubmit && Boolean(fieldState.error)
              return (
                <FormControl error={hasError} sx={requiredCheckboxGroupItemSx}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(field.value)}
                        onChange={(e) => field.onChange(e.target.checked)}
                        sx={requiredCheckboxSx(hasError)}
                      />
                    }
                    label={
                      <>
                        <Box component="span" sx={requiredCheckboxLabelTextSx}>
                          {t(
                            'app:device.calibration.verifyStep.zklRc.shortCircuitPresent',
                          )}
                        </Box>
                        <Box
                          component="span"
                          sx={{ color: 'error.main', ml: 0.5 }}
                        >
                          *
                        </Box>
                      </>
                    }
                  />
                  {hasAttemptedSubmit && showFieldError(fieldState) && (
                    <FormHelperText sx={{ ml: 4 }}>
                      {showFieldError(fieldState)}
                    </FormHelperText>
                  )}
                </FormControl>
              )
            }}
          />

          <Controller
            name="shortCircuitWire"
            control={methods.control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                type="number"
                label={t(
                  'app:device.calibration.verifyStep.zklRc.shortCircuitWire',
                )}
                fullWidth
                variant="standard"
                error={hasAttemptedSubmit && Boolean(fieldState.error)}
                helperText={
                  hasAttemptedSubmit ? showFieldError(fieldState) : undefined
                }
                slotProps={{
                  inputLabel: {
                    sx: { ...requiredIndicator, ...labelNoErrorColor },
                  },
                  htmlInput: {
                    autoComplete: 'off',
                    min: 0,
                    max: MAX_SHORT_CIRCUIT_WIRE_RESISTANCE,
                    step: 'any',
                  },
                }}
                sx={{ mt: 1, mb: 1 }}
              />
            )}
          />

          {isRC && (
            <Controller
              name="shortCircuitRemoved"
              control={methods.control}
              render={({ field, fieldState }) => {
                const hasError = hasAttemptedSubmit && Boolean(fieldState.error)
                return (
                  <FormControl
                    error={hasError}
                    sx={requiredCheckboxGroupItemSx}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(field.value)}
                          onChange={(e) => field.onChange(e.target.checked)}
                          sx={requiredCheckboxSx(hasError)}
                        />
                      }
                      label={
                        <>
                          <Box
                            component="span"
                            sx={requiredCheckboxLabelTextSx}
                          >
                            {t(
                              'app:device.calibration.verifyStep.zklRc.shortCircuitRemoved',
                            )}
                          </Box>
                          <Box
                            component="span"
                            sx={{ color: 'error.main', ml: 0.5 }}
                          >
                            *
                          </Box>
                        </>
                      }
                    />
                    {hasAttemptedSubmit && showFieldError(fieldState) && (
                      <FormHelperText sx={{ ml: 4 }}>
                        {showFieldError(fieldState)}
                      </FormHelperText>
                    )}
                  </FormControl>
                )
              }}
            />
          )}
        </FormGroup>

        <Controller
          name="remarks"
          control={methods.control}
          render={({ field }) => (
            <TextField
              {...field}
              label={t('app:device.calibration.verifyStep.remarks')}
              fullWidth
              variant="standard"
              multiline
              minRows={1}
              maxRows={6}
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ mt: 1 }}
            />
          )}
        />

        <ConfirmDialog
          open={showConfirmDialog}
          setOpen={setShowConfirmDialog}
          onConfirm={() => {
            const remarks = methods.getValues('remarks')
            confirm({
              // The backend resolves the box by its bare label - the decorated
              // label is for display only.
              resistanceBox: resistanceBox?.label ?? 'Unknown',
              jumperBox: jumperBox ?? 'Unknown',
              remark: remarks,
              deviceType: device.type,
            })
            setShowConfirmDialog(false)
          }}
          onCancel={() => setShowConfirmDialog(false)}
          onClose={() => setShowConfirmDialog(false)}
        >
          {t('app:device.calibration.verifyStep.confirm')}
        </ConfirmDialog>
      </VerifyStepBase>
    </FormProvider>
  )
}

export default ZklVerifyStep
